// Hook to fetch/put/post/delete the /userCustomTableView response

/* Shared */
import {
    CustomTableView,
    DeleteUserCustomTableViewResult,
    GetUserCustomTableViewResult,
    PostPutUserCustomTableViewParams
} from 'application/Shared/Components/Table/types';
import { Response } from 'application/Shared/Helpers';
import { debug } from 'application/Shared/Utils';

/*  NPM  */
import axios from 'axios';

/*  Hooks  */
import { useSession } from 'hooks/redux/Selectors/session';

/*  React  */
import type { TFunction } from 'i18next';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Custom hook for fetching a customer table view
 * @returns An object containing the customTableViews and a loading state
 */
export const useFetchUserCustomTableViews = (
    targetTable: string
): GetUserCustomTableViewResult => {
    const [customTableViews, setCustomTableViews] = useState<
        Array<CustomTableView>
    >([]);
    const [loading, setLoading] = useState<boolean>(true);
    // calling refetchData will force a rerender on the hook
    const [shouldRefetchData, setShouldRefetchData] = useState({});

    const refetchCustomTableViews = () => setShouldRefetchData({});

    useEffect(() => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        /**
         * Runs a customer recipe with the given parameters
         * @returns {Promise<void>} A promise that resolves to the result of getting the view
         */
        const fetchData = async (): Promise<void> => {
            setLoading(true);
            try {
                const response = await axios.get('/userCustomTableView', {
                    cancelToken: source.token,
                    params: { size: -1, targetTable }
                });
                if (!Response.hasResponseData(response)) {
                    debug.e('Unable to get customTableViews. No data.');
                } else {
                    setCustomTableViews(Response.getData(response));
                }
            } catch (error) {
                debug.e('Unable to get customTableViews.', error);
            }
            setLoading(false);
        };

        fetchData();

        return () => {
            // Cancel the request when the component unmounts
            source.cancel();
        };
    }, [shouldRefetchData, targetTable]);

    return {
        customTableViews,
        loading,
        refetchCustomTableViews
    };
};

// Hook to fetch the /application data async
export const useFetchUserCustomTableViewsAsync = () => {
    const error = useRef<unknown | null>(null);
    const customTableViews = useRef<Array<CustomTableView>>([]);

    const getUserCustomTableViews = useCallback(async (targetTable: string) => {
        const { CancelToken } = axios;
        const source = CancelToken.source();

        try {
            const response = await axios.get('/userCustomTableView', {
                cancelToken: source.token,
                params: { size: -1, targetTable }
            });
            if (!Response.hasResponseData(response)) {
                debug.e('Unable to get customTableViews. No data.');
            } else {
                customTableViews.current = Response.getData(response);
            }
        } catch (err) {
            error.current = err;
            debug.e('Error getting customTableViews information.', err);
        }
        return {
            customTableViews: customTableViews.current,
            error: error.current
        };
    }, []);
    return { getUserCustomTableViews };
};

export const usePutUserCustomTableView = (t: TFunction) => {
    const [saving, setSaving] = useState(false);

    const putUserCustomTableView = useCallback(
        async (params: PostPutUserCustomTableViewParams) => {
            let data: string | null = null;
            let error: string | null = null;
            try {
                setSaving(true);
                const response = await axios.put(
                    `/userCustomTableView/${params.id}`,
                    params
                );
                data = Response.getId(response);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                debug.e(
                    t('TableViews.userCustomTableView.edit.error', {
                        displayName: params.displayName
                    }),
                    err
                );
                error = err;
            } finally {
                setSaving(false);
            }
            return {
                data,
                error
            };
        },
        [t]
    );

    return { putUserCustomTableView, saving };
};

export const usePostUserCustomTableView = (t: TFunction) => {
    const session = useSession();
    const [saving, setSaving] = useState(false);

    const postUserCustomTableView = useCallback(
        async (params: PostPutUserCustomTableViewParams) => {
            let data: string | null = null;
            let error: string | null = null;
            try {
                setSaving(true);
                const response = await axios.post('/userCustomTableView', {
                    ...params,
                    platformUserId: session.id
                });
                data = Response.getId(response);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                debug.e(
                    t('TableViews.userCustomTableView.saveAsNewView.error', {
                        displayName: params.displayName
                    }),
                    err
                );
                error = err;
            } finally {
                setSaving(false);
            }
            return {
                data,
                error
            };
        },
        [session.id, t]
    );

    return { postUserCustomTableView, saving };
};

export const useDeleteUserCustomTableView = (t: TFunction) => {
    const [saving, setSaving] = useState(false);

    const deleteUserCustomTableView = useCallback(
        async (customTableViewId: string) => {
            let data: DeleteUserCustomTableViewResult | null = null;
            let error: string | null = null;
            try {
                setSaving(true);
                const response = await axios.delete(
                    `/userCustomTableView/${customTableViewId}`
                );
                data = Response.get(response);
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
            } catch (err: any) {
                error = err;
                debug.e(
                    t('TableViews.userCustomTableView.delete.error', {
                        displayName: 'Custom view'
                    }),
                    err
                );
            } finally {
                setSaving(false);
            }
            return { data, error };
        },
        [t]
    );

    return { deleteUserCustomTableView, saving };
};
